import React, { FC, useState, useMemo } from 'react';
import {
  AnyFCO,
  FeatureViewFCO,
  FeatureViewFCOFields,
  FCOFields,
  FCOType,
  FeatureViewFCOType,
} from '../../../core/types/fcoTypes';

import {
  ComboFilterCategory,
  ComboFilterDateSelector,
  ComboFilterHierarchicalList,
  ComboFilterHierarchyState,
  ComboFilterListSelector,
  ComboFilterRangeSelector,
  EmptyProps,
  ExtendedTableColumn,
  IconType,
  Link,
  Select,
  Table,
  TableViewLayout,
  Text,
  useTableFilters,
} from '@lithos';

import {
  isInBounds,
  isInDateRange,
  getFilteredItems,
  tableStringSortParameters,
  tableArrayLengthSortParameters,
  tableDateSortParameters,
  tableBooleanSortParameters,
} from '@lithos/TableUtils';

import { isEqual, startCase, uniq } from 'lodash';

import RelatedFCOsCell from '../fcoListView/RelatedFCOsCell';
import TagsCell from '../fcoListView/TagsCell';
import DateCell from '../fcoListView/DateCell';
import MaterializationCell from '../fcoListView/MaterializationCell';
import { areAnyCategoryMembersCurrentlyActive, countHierarchyActiveSelections } from '@lithos/ComboFilterUtils';
import { defaultDateState, defaultRangeState, defaultTagsState } from '../fcoListView/utils';
import TableSelectors from '../fcoListView/TableSelectors';
import TableColumnControls from '../fcoListView/TableColumnControls';
import { FeatureFlags, useUserSettings } from '../../context/UserSettingsContext';

interface FeatureViewsListProps {
  items: FeatureViewFCO[];
  idToFcoMap: Record<string, AnyFCO>;
  workspace: string;
}

enum FeatureViewsListColumnKeys {
  NAME,
  TAGS,
  TYPE,
  ONLINE,
  OFFLINE,
  NUMBER_OF_FEATURES,
  NUMBER_OF_DATA_SOURCES,
  DATA_SOURCES,
  NUMBER_OF_ENTITIES,
  ENTITIES,
  NUMBER_OF_TRANSFORMATIONS,
  TRANSFORMATIONS,
  NUMBER_OF_FEATURE_SERVICES,
  FEATURE_SERVICES,
  CREATED_BY,
  CREATED_AT,
  LAST_UPDATED_BY,
  LAST_UPDATED_AT,
  SERVER_GROUP,
}

interface FeatureViewsListFilterState {
  tags: ComboFilterHierarchyState;
  type: string[];
  numberOfFeatures: number[];
  online: string[];
  offline: string[];
  '# of Features': [number, number];
  '# of Data Sources': [number, number];
  'Data Sources': string[];
  '# of Entities': [number, number];
  Entities: string[];
  '# of Transformations': [number, number];
  Transformations: string[];
  '# of Feature Services': [number, number];
  'Feature Services': string[];
  createdBy: string[];
  createdAt: [Date, Date];
  lastUpdatedBy: string[];
  lastUpdatedAt: [Date, Date];
  serverGroup: string[];
}

const FeatureViewsList: FC<FeatureViewsListProps> = ({ items, idToFcoMap, workspace }) => {
  const { featureEnabled } = useUserSettings();
  const canShowServerGroups = featureEnabled(FeatureFlags.SHOW_SERVER_GROUPS);

  // Always need to set a default filter state
  const defaultFilterState: FeatureViewsListFilterState = useMemo(
    (): FeatureViewsListFilterState => ({
      tags: defaultTagsState(items),
      type: [],
      numberOfFeatures: [],
      online: [],
      offline: [],
      '# of Features': defaultRangeState(items, FeatureViewFCOFields.FEATURES),
      '# of Data Sources': defaultRangeState(items, FeatureViewFCOFields.DATA_SOURCE_IDS),
      'Data Sources': [],
      '# of Entities': defaultRangeState(items, FeatureViewFCOFields.ENTITY_IDS),
      Entities: [],
      '# of Transformations': defaultRangeState(items, FeatureViewFCOFields.ALL_TRANSFORMATIONS),
      Transformations: [],
      '# of Feature Services': defaultRangeState(items, FeatureViewFCOFields.FEATURE_SERVICES),
      'Feature Services': [],
      createdBy: [],
      createdAt: defaultDateState(items, FCOFields.CREATED_DATE),
      lastUpdatedBy: [],
      lastUpdatedAt: defaultDateState(items, FCOFields.LAST_MODIFIED_DATE),
      serverGroup: [],
    }),
    [items]
  );

  const allValidOptions = useMemo(() => {
    return {
      Tags: Array.from(Object.keys(defaultTagsState(items))),
      Type: uniq(items.map((item) => item[FeatureViewFCOFields.FEATURE_VIEW_TYPE])),
      'Data Sources': uniq(items.map((item) => item[FeatureViewFCOFields.DATA_SOURCE_IDS]).flat())
        .filter((id): id is string => id !== undefined)
        .map((id) => idToFcoMap[id]?.[FCOFields.NAME] ?? '')
        .filter(Boolean),
      Entities: uniq(items.map((item) => item[FeatureViewFCOFields.ENTITY_IDS]).flat())
        .filter((id): id is string => id !== undefined)
        .map((id) => idToFcoMap[id]?.[FCOFields.NAME] ?? '')
        .filter(Boolean),
      Transformations: uniq(items.map((item) => item[FeatureViewFCOFields.ALL_TRANSFORMATIONS]).flat())
        .filter((id): id is string => id !== undefined)
        .map((id) => idToFcoMap[id]?.[FCOFields.NAME] ?? '')
        .filter(Boolean),
      'Feature Services': uniq(items.map((item) => item[FeatureViewFCOFields.FEATURE_SERVICES]).flat())
        .filter((id): id is string => id !== undefined)
        .map((id) => idToFcoMap[id]?.[FCOFields.NAME] ?? '')
        .filter(Boolean),
      Owner: uniq(items.map((item) => item[FCOFields.OWNER] ?? '')).filter(Boolean),
      'Last Updated By': uniq(items.map((item) => item[FCOFields.LAST_MODIFIED_BY] ?? '')).filter(Boolean),
      'Transform Server Group': uniq(
        items.map((item) => item[FeatureViewFCOFields.TRANSFORM_SERVER_GROUP_NAME] ?? '')
      ).filter(Boolean),
    };
  }, [items, idToFcoMap]);

  // Shared logic and states for filtered table views
  const {
    searchQuery,
    hasAnyFilters,
    activeFilterCount,
    sortType,
    setSortType,
    activeHeading,
    setActiveHeading,
    filterState,
    onSearchInputChange,
    handleResetFilters,
    handleFilterChange,
  } = useTableFilters<FeatureViewFCO, FeatureViewsListColumnKeys, FeatureViewsListFilterState>(defaultFilterState);

  // Filter components are the actual input components (e.g. A combo filter listt)
  // These are defined separately from the ComboFilters because they are reused in the table header
  // They may also be reused in shortcut popovers in the general filter list
  // This allows us to reference the same component in multiple places
  const filterComponents: Record<
    FeatureViewsListColumnKeys,
    {
      filter: React.ReactNode;
      activeCount: () => number;
      filterCallback: (featureView: FeatureViewFCO) => boolean;
      name: string;
    }
  > = useMemo(() => {
    return {
      [FeatureViewsListColumnKeys.NAME]: {
        filter: <></>,
        activeCount: () => 0,
        filterCallback: () => true,
        name: 'Name',
      },
      [FeatureViewsListColumnKeys.TAGS]: {
        filter: (
          <ComboFilterHierarchicalList
            items={filterState.tags}
            setItems={(tags) => {
              handleFilterChange(filterComponents, 'tags', tags);
            }}
          />
        ),
        activeCount: () => countHierarchyActiveSelections(filterState.tags),
        filterCallback: (featureView) => {
          let hasEachCategoryMatched = true;
          const categories = Object.keys(filterState.tags);
          categories.forEach((category) => {
            if (areAnyCategoryMembersCurrentlyActive(filterState.tags[category])) {
              const activeSubcategories = Object.entries(filterState.tags[category])
                .filter((entry) => entry[1])
                .map((result) => result[0]);
              const hasMatch = featureView.tags.find(
                (tag) => tag.key === category && activeSubcategories.includes(tag.value)
              );
              if (hasMatch === undefined) {
                hasEachCategoryMatched = false;
              }
            }
          });

          return hasEachCategoryMatched;
        },
        name: 'Tags',
      },
      [FeatureViewsListColumnKeys.TYPE]: {
        filter: (
          <ComboFilterListSelector
            selected={filterState.type}
            setSelected={(type) => {
              handleFilterChange(filterComponents, 'type', type);
            }}
            items={allValidOptions.Type.map(startCase)}
            title={''}
          />
        ),
        activeCount: () => filterState.type.length,
        filterCallback: (featureView) => {
          return filterState.type.includes(startCase(featureView[FeatureViewFCOFields.FEATURE_VIEW_TYPE]));
        },
        name: 'Type',
      },
      [FeatureViewsListColumnKeys.ONLINE]: {
        filter: (
          <ComboFilterListSelector
            selected={filterState.online}
            setSelected={(online) => handleFilterChange(filterComponents, 'online', online)}
            items={['true', 'false']}
            title={''}
            customRenderer={(name) => {
              return <MaterializationCell materialization={name === 'true'} />;
            }}
          />
        ),
        activeCount: () => filterState.online.length,
        filterCallback: (featureView) => {
          const isEnabled = featureView[FeatureViewFCOFields.IS_ONLINE_MATERIALIZATION_ENABLED] ?? false;
          return filterState.online.includes(isEnabled.toString());
        },
        name: 'Online',
      },
      [FeatureViewsListColumnKeys.OFFLINE]: {
        filter: (
          <ComboFilterListSelector
            selected={filterState.offline}
            setSelected={(offline) => handleFilterChange(filterComponents, 'offline', offline)}
            items={['true', 'false']}
            title={''}
            customRenderer={(name) => {
              return <MaterializationCell materialization={name === 'true'} />;
            }}
          />
        ),
        activeCount: () => filterState.offline.length,
        filterCallback: (featureView) => {
          const isEnabled = featureView[FeatureViewFCOFields.IS_OFFLINE_MATERIALIZATION_ENABLED] ?? false;
          return filterState.offline.includes(isEnabled.toString());
        },
        name: 'Offline',
      },
      [FeatureViewsListColumnKeys.NUMBER_OF_FEATURES]: {
        filter: (
          <ComboFilterRangeSelector
            value={filterState['# of Features']}
            setValue={(range) => {
              handleFilterChange(filterComponents, '# of Features', range);
            }}
            bounds={defaultFilterState['# of Features']}
          />
        ),
        activeCount: () => (isEqual(filterState['# of Features'], defaultFilterState['# of Features']) ? 0 : 1),
        filterCallback: (featureView) => {
          return isInBounds(featureView[FeatureViewFCOFields.FEATURES].length, filterState['# of Features']);
        },
        name: '# of Features',
      },
      [FeatureViewsListColumnKeys.NUMBER_OF_DATA_SOURCES]: {
        filter: (
          <ComboFilterRangeSelector
            value={filterState['# of Data Sources']}
            setValue={(range) => {
              handleFilterChange(filterComponents, '# of Data Sources', range);
            }}
            bounds={defaultFilterState['# of Data Sources']}
          />
        ),
        activeCount: () => (isEqual(filterState['# of Data Sources'], defaultFilterState['# of Data Sources']) ? 0 : 1),
        filterCallback: (featureView) => {
          return isInBounds(featureView[FeatureViewFCOFields.DATA_SOURCE_IDS].length, filterState['# of Data Sources']);
        },
        name: '# of Data Sources',
      },
      [FeatureViewsListColumnKeys.DATA_SOURCES]: {
        filter: (
          <ComboFilterListSelector
            selected={filterState['Data Sources']}
            setSelected={(dataSources) => handleFilterChange(filterComponents, 'Data Sources', dataSources)}
            items={allValidOptions['Data Sources']}
            title={''}
          />
        ),
        activeCount: () => filterState['Data Sources'].length,
        filterCallback: (featureView) => {
          const dataSourceIds = featureView[FeatureViewFCOFields.DATA_SOURCE_IDS] ?? [];
          const dataSourceNames = dataSourceIds.map((id) => idToFcoMap[id]?.[FCOFields.NAME] ?? '').filter(Boolean);
          return filterState['Data Sources'].some((name) => dataSourceNames.includes(name));
        },
        name: 'Data Sources',
        icon: 'Data Source' as const,
      },
      [FeatureViewsListColumnKeys.NUMBER_OF_ENTITIES]: {
        filter: (
          <ComboFilterRangeSelector
            value={filterState['# of Entities']}
            setValue={(range) => {
              handleFilterChange(filterComponents, '# of Entities', range);
            }}
            bounds={defaultFilterState['# of Entities']}
          />
        ),
        activeCount: () => (isEqual(filterState['# of Entities'], defaultFilterState['# of Entities']) ? 0 : 1),
        filterCallback: (featureView) => {
          return isInBounds(featureView[FeatureViewFCOFields.ENTITY_IDS].length, filterState['# of Entities']);
        },
        name: '# of Entities',
      },
      [FeatureViewsListColumnKeys.ENTITIES]: {
        filter: (
          <ComboFilterListSelector
            selected={filterState['Entities']}
            setSelected={(entities) => handleFilterChange(filterComponents, 'Entities', entities)}
            items={allValidOptions['Entities']}
            title={''}
          />
        ),
        activeCount: () => filterState['Entities'].length,
        filterCallback: (featureView) => {
          const entityIds = featureView[FeatureViewFCOFields.ENTITY_IDS] ?? [];
          const entityNames = entityIds.map((id) => idToFcoMap[id]?.[FCOFields.NAME] ?? '').filter(Boolean);
          return filterState['Entities'].some((name) => entityNames.includes(name));
        },
        name: 'Entities',
        icon: 'Entity' as const,
      },
      [FeatureViewsListColumnKeys.NUMBER_OF_TRANSFORMATIONS]: {
        filter: (
          <ComboFilterRangeSelector
            value={filterState['# of Transformations']}
            setValue={(range) => {
              handleFilterChange(filterComponents, '# of Transformations', range);
            }}
            bounds={defaultFilterState['# of Transformations']}
          />
        ),
        activeCount: () => filterState['# of Transformations'].length,
        filterCallback: (featureView) => {
          const numTransformations = featureView[FeatureViewFCOFields.ALL_TRANSFORMATIONS]?.length ?? 0;
          return (
            numTransformations >= filterState['# of Transformations'][0] &&
            numTransformations <= filterState['# of Transformations'][1]
          );
        },
        name: '# of Transformations',
        icon: 'Transformations' as const,
      },
      [FeatureViewsListColumnKeys.TRANSFORMATIONS]: {
        filter: (
          <ComboFilterListSelector
            selected={filterState['Transformations']}
            setSelected={(transformations) => handleFilterChange(filterComponents, 'Transformations', transformations)}
            items={allValidOptions['Transformations']}
            title={''}
          />
        ),
        activeCount: () => filterState['Transformations'].length,
        filterCallback: (featureView) => {
          const transformationIds = featureView[FeatureViewFCOFields.ALL_TRANSFORMATIONS] ?? [];
          const transformationNames = transformationIds
            .map((id) => idToFcoMap[id]?.[FCOFields.NAME] ?? '')
            .filter(Boolean);
          return filterState['Transformations'].some((name) => transformationNames.includes(name));
        },
        name: 'Transformations',
        icon: 'Transformations' as const,
      },
      [FeatureViewsListColumnKeys.NUMBER_OF_FEATURE_SERVICES]: {
        filter: (
          <ComboFilterRangeSelector
            value={filterState['# of Feature Services']}
            setValue={(range) => {
              handleFilterChange(filterComponents, '# of Feature Services', range);
            }}
            bounds={defaultFilterState['# of Feature Services']}
          />
        ),
        activeCount: () =>
          isEqual(filterState['# of Feature Services'], defaultFilterState['# of Feature Services']) ? 0 : 1,
        filterCallback: (featureView) => {
          return isInBounds(
            featureView[FeatureViewFCOFields.FEATURE_SERVICES].length,
            filterState['# of Feature Services']
          );
        },
        name: '# of Feature Services',
      },
      [FeatureViewsListColumnKeys.FEATURE_SERVICES]: {
        filter: (
          <ComboFilterListSelector
            selected={filterState['Feature Services']}
            setSelected={(featureServices) => handleFilterChange(filterComponents, 'Feature Services', featureServices)}
            items={allValidOptions['Feature Services']}
            title={''}
          />
        ),
        activeCount: () => filterState['Feature Services'].length,
        filterCallback: (featureView) => {
          const featureServiceIds = featureView[FeatureViewFCOFields.FEATURE_SERVICES] ?? [];
          const featureServiceNames = featureServiceIds
            .map((id) => idToFcoMap[id]?.[FCOFields.NAME] ?? '')
            .filter(Boolean);
          return filterState['Feature Services'].some((name) => featureServiceNames.includes(name));
        },
        name: 'Feature Services',
        icon: 'Feature Service' as const,
      },
      [FeatureViewsListColumnKeys.LAST_UPDATED_BY]: {
        filter: (
          <ComboFilterListSelector
            selected={filterState.lastUpdatedBy}
            setSelected={(type) => handleFilterChange(filterComponents, 'lastUpdatedBy', type)}
            items={allValidOptions['Last Updated By']}
            title={''}
          />
        ),
        activeCount: () => filterState.lastUpdatedBy.length,
        filterCallback: (featureView) => {
          return filterState.lastUpdatedBy.includes(featureView[FCOFields.LAST_MODIFIED_BY] ?? '');
        },
        name: 'Last Updated By',
      },
      [FeatureViewsListColumnKeys.LAST_UPDATED_AT]: {
        filter: (
          <ComboFilterDateSelector
            start={filterState.lastUpdatedAt[0]}
            setStart={(date) => {
              handleFilterChange(filterComponents, 'lastUpdatedAt', [date as Date, filterState.lastUpdatedAt[1]]);
            }}
            end={filterState.lastUpdatedAt[1]}
            setEnd={(date) => {
              handleFilterChange(filterComponents, 'lastUpdatedAt', [filterState.lastUpdatedAt[0], date as Date]);
            }}
          />
        ),
        activeCount: () => (isEqual(filterState.lastUpdatedAt, defaultFilterState.lastUpdatedAt) ? 0 : 1),
        filterCallback: (featureView) => {
          return isInDateRange(featureView[FCOFields.LAST_MODIFIED_DATE], filterState.lastUpdatedAt);
        },
        name: 'Last Updated At',
      },
      [FeatureViewsListColumnKeys.CREATED_BY]: {
        filter: (
          <ComboFilterListSelector
            selected={filterState.createdBy}
            setSelected={(type) => handleFilterChange(filterComponents, 'createdBy', type)}
            items={allValidOptions['Owner']}
            title={''}
          />
        ),
        activeCount: () => filterState.createdBy.length,
        filterCallback: (featureView) => {
          return filterState.createdBy.includes(featureView[FCOFields.OWNER] ?? '');
        },
        name: 'Owner',
      },
      [FeatureViewsListColumnKeys.CREATED_AT]: {
        filter: (
          <ComboFilterDateSelector
            start={filterState.createdAt[0]}
            setStart={(date) => {
              handleFilterChange(filterComponents, 'createdAt', [date as Date, filterState.createdAt[1]]);
            }}
            end={filterState.lastUpdatedAt[1]}
            setEnd={(date) => {
              handleFilterChange(filterComponents, 'createdAt', [filterState.createdAt[0], date as Date]);
            }}
          />
        ),
        activeCount: () => (isEqual(filterState.createdAt, defaultFilterState.createdAt) ? 0 : 1),
        filterCallback: (featureView) => {
          return isInDateRange(featureView[FCOFields.CREATED_DATE], filterState.createdAt);
        },
        name: 'Created At',
      },
      [FeatureViewsListColumnKeys.SERVER_GROUP]: {
        filter: (
          <ComboFilterListSelector
            selected={filterState.serverGroup}
            setSelected={(serverGroup) => handleFilterChange(filterComponents, 'serverGroup', serverGroup)}
            items={allValidOptions['Transform Server Group']}
            title={''}
          />
        ),
        activeCount: () => filterState.serverGroup.length,
        filterCallback: (featureView) => {
          return filterState.serverGroup.includes(featureView[FeatureViewFCOFields.TRANSFORM_SERVER_GROUP_NAME] ?? '');
        },
        name: 'Transform Server Group',
      },
    };
  }, [filterState, idToFcoMap, defaultFilterState, handleFilterChange, allValidOptions]);

  // Memoize active filters to prevent recalculation
  const activeFilters = useMemo(
    () => Object.values(filterComponents).filter((filter) => filter.activeCount() !== 0),
    [filterComponents]
  );

  // Memoize the filtered and sorted results
  const filteredResults = useMemo(
    () => getFilteredItems(items, activeFilters, [FCOFields.NAME, FCOFields.ID], searchQuery ?? '', sortType),
    [items, activeFilters, searchQuery, sortType]
  );

  // These define the filter categories in the Filter button popover
  const categories: ComboFilterCategory[] = [
    {
      name: 'Tags',
      icon: 'Tag',
      resetCallback: () => {
        handleFilterChange(filterComponents, 'tags', defaultFilterState.tags);
      },
      activeCount: countHierarchyActiveSelections(filterState.tags),
      popover: filterComponents[FeatureViewsListColumnKeys.TAGS].filter,
      disabled: allValidOptions['Tags'].length === 0,
    },
    {
      name: 'Type',
      icon: 'FeatureView',
      resetCallback: () => {
        handleFilterChange(filterComponents, 'type', defaultFilterState.type);
      },
      activeCount: filterState.type.length,
      popover: filterComponents[FeatureViewsListColumnKeys.TYPE].filter,
    },
    {
      name: 'Online',
      icon: 'FeatureView',
      resetCallback: () => {
        handleFilterChange(filterComponents, 'online', defaultFilterState.online);
      },
      activeCount: filterState.online.length,
      popover: filterComponents[FeatureViewsListColumnKeys.ONLINE].filter,
    },
    {
      name: 'Offline',
      icon: 'FeatureView',
      resetCallback: () => {
        handleFilterChange(filterComponents, 'offline', defaultFilterState.offline);
      },
      activeCount: filterState.offline.length,
      popover: filterComponents[FeatureViewsListColumnKeys.OFFLINE].filter,
    },
    {
      name: '# of Features',
      icon: 'FeatureView',
      resetCallback: () => {
        handleFilterChange(filterComponents, '# of Features', defaultFilterState['# of Features']);
      },
      activeCount: isEqual(filterState['# of Features'], defaultFilterState['# of Features']) ? 0 : 1,
      popover: filterComponents[FeatureViewsListColumnKeys.NUMBER_OF_FEATURES].filter,
    },
    {
      name: '# of Data Sources',
      icon: 'Sources',
      resetCallback: () => {
        handleFilterChange(filterComponents, '# of Data Sources', defaultFilterState['# of Data Sources']);
      },
      activeCount: isEqual(filterState['# of Data Sources'], defaultFilterState['# of Data Sources']) ? 0 : 1,
      popover: filterComponents[FeatureViewsListColumnKeys.NUMBER_OF_DATA_SOURCES].filter,
    },
    {
      name: 'Data Sources',
      icon: 'Sources',
      resetCallback: () => {
        handleFilterChange(filterComponents, 'Data Sources', defaultFilterState['Data Sources']);
      },
      activeCount: filterState['Data Sources'].length,
      popover: filterComponents[FeatureViewsListColumnKeys.DATA_SOURCES].filter,
      disabled: allValidOptions['Data Sources'].length === 0,
    },
    {
      name: '# of Entities',
      icon: 'Entities',
      resetCallback: () => {
        handleFilterChange(filterComponents, '# of Entities', defaultFilterState['# of Entities']);
      },
      activeCount: isEqual(filterState['# of Entities'], defaultFilterState['# of Entities']) ? 0 : 1,
      popover: filterComponents[FeatureViewsListColumnKeys.NUMBER_OF_ENTITIES].filter,
    },
    {
      name: 'Entities',
      icon: 'Entities',
      resetCallback: () => {
        handleFilterChange(filterComponents, 'Entities', defaultFilterState['Entities']);
      },
      activeCount: filterState['Entities'].length,
      popover: filterComponents[FeatureViewsListColumnKeys.ENTITIES].filter,
      disabled: allValidOptions['Entities'].length === 0,
    },
    {
      name: '# of Transformations',
      icon: 'Transformations',
      resetCallback: () => {
        handleFilterChange(filterComponents, '# of Transformations', defaultFilterState['# of Transformations']);
      },
      activeCount: isEqual(filterState['# of Transformations'], defaultFilterState['# of Transformations']) ? 0 : 1,
      popover: filterComponents[FeatureViewsListColumnKeys.NUMBER_OF_TRANSFORMATIONS].filter,
    },
    {
      name: 'Transformations',
      icon: 'Transformations',
      resetCallback: () => {
        handleFilterChange(filterComponents, 'Transformations', defaultFilterState['Transformations']);
      },
      activeCount: filterState['Transformations'].length,
      popover: filterComponents[FeatureViewsListColumnKeys.TRANSFORMATIONS].filter,
      disabled: allValidOptions['Transformations'].length === 0,
    },
    {
      name: '# of Feature Services',
      icon: 'FeatureService',
      resetCallback: () => {
        handleFilterChange(filterComponents, '# of Feature Services', defaultFilterState['# of Feature Services']);
      },
      activeCount: isEqual(filterState['# of Feature Services'], defaultFilterState['# of Feature Services']) ? 0 : 1,
      popover: filterComponents[FeatureViewsListColumnKeys.NUMBER_OF_FEATURE_SERVICES].filter,
    },
    {
      name: 'Feature Services',
      icon: 'FeatureService',
      resetCallback: () => {
        handleFilterChange(filterComponents, 'Feature Services', defaultFilterState['Feature Services']);
      },
      activeCount: filterState['Feature Services'].length,
      popover: filterComponents[FeatureViewsListColumnKeys.FEATURE_SERVICES].filter,
      disabled: allValidOptions['Feature Services'].length === 0,
    },
    {
      name: 'Owner',
      icon: 'UserCircle',
      resetCallback: () => {
        handleFilterChange(filterComponents, 'createdBy', defaultFilterState.createdBy);
      },
      activeCount: filterState.createdBy.length,
      popover: filterComponents[FeatureViewsListColumnKeys.CREATED_BY].filter,
      disabled: allValidOptions['Owner'].length === 0,
    },
    {
      name: 'Created At',
      icon: 'Calendar',
      resetCallback: () => {
        handleFilterChange(filterComponents, 'createdAt', defaultFilterState.createdAt);
      },
      activeCount: isEqual(filterState.createdAt, defaultFilterState.createdAt) ? 0 : 1,
      popover: filterComponents[FeatureViewsListColumnKeys.CREATED_AT].filter,
    },
    {
      name: 'Last Updated By',
      icon: 'UserCircle',
      resetCallback: () => {
        handleFilterChange(filterComponents, 'lastUpdatedBy', defaultFilterState.lastUpdatedBy);
      },
      activeCount: filterState.lastUpdatedBy.length,
      popover: filterComponents[FeatureViewsListColumnKeys.LAST_UPDATED_BY].filter,
      disabled: allValidOptions['Last Updated By'].length === 0,
    },
    {
      name: 'Last Updated At',
      icon: 'Calendar',
      resetCallback: () => {
        handleFilterChange(filterComponents, 'lastUpdatedAt', defaultFilterState.lastUpdatedAt);
      },
      activeCount: isEqual(filterState.lastUpdatedAt, defaultFilterState.lastUpdatedAt) ? 0 : 1,
      popover: filterComponents[FeatureViewsListColumnKeys.LAST_UPDATED_AT].filter,
    },
    ...(canShowServerGroups
      ? [
          {
            name: 'Transform Server Group',
            icon: 'TransformServerGroup' as IconType,
            resetCallback: () => {
              handleFilterChange(filterComponents, 'serverGroup', defaultFilterState.serverGroup);
            },
            activeCount: filterState.serverGroup.length,
            popover: filterComponents[FeatureViewsListColumnKeys.SERVER_GROUP].filter,
          },
        ]
      : []),
  ];

  // Define the columns for the table
  const columns: ExtendedTableColumn<FeatureViewFCO>[] = [
    {
      name: 'Name',
      id: FeatureViewsListColumnKeys.NAME,
      content: (featureView) => (
        <Link
          name={featureView[FCOFields.NAME]}
          icon="FeatureView"
          url={`/repo/${workspace}/features/${featureView[FCOFields.NAME]}/overview`}
          testId={`features-link-${featureView[FCOFields.NAME]}`}
        />
      ),
      columnWidth: 'l',
      ...tableStringSortParameters(sortType, FCOFields.NAME, setSortType),
    },
    {
      name: 'Tags',
      id: FeatureViewsListColumnKeys.TAGS,
      content: (featureView) => <TagsCell fco={featureView} />,
      filterContent: filterComponents[FeatureViewsListColumnKeys.TAGS].filter,
      ...tableArrayLengthSortParameters(sortType, FCOFields.TAGS, setSortType),
    },
    {
      name: 'Type',
      id: FeatureViewsListColumnKeys.TYPE,
      content: (featureView) => {
        const fvTypeMap: Record<FeatureViewFCOType, string> = {
          [FeatureViewFCOType.STREAM]: 'Stream',
          [FeatureViewFCOType.BATCH]: 'Batch',
          [FeatureViewFCOType.STREAM_WINDOW_AGGREGATE]: 'Stream',
          [FeatureViewFCOType.BATCH_WINDOW_AGGREGATE]: 'Batch',
          [FeatureViewFCOType.REALTIME]: 'Realtime',
          [FeatureViewFCOType.FEATURE_TABLE]: 'Feature Table',
          [FeatureViewFCOType.UNKNOWN]: '',
          [FeatureViewFCOType.PUSH_WITH_BATCH]: 'Push',
          [FeatureViewFCOType.PUSH_NO_BATCH]: 'Push',
        };
        return <Text size="s">{fvTypeMap[featureView[FeatureViewFCOFields.FEATURE_VIEW_TYPE]]}</Text>;
      },
      columnWidth: 's',
      filterContent: filterComponents[FeatureViewsListColumnKeys.TYPE].filter,
      ...tableStringSortParameters(sortType, FeatureViewFCOFields.FEATURE_VIEW_TYPE, setSortType),
    },
    {
      name: '# of Features',
      id: FeatureViewsListColumnKeys.NUMBER_OF_FEATURES,
      content: (featureView) => {
        return <Text size="s">{featureView[FeatureViewFCOFields.FEATURES].length}</Text>;
      },
      columnWidth: 's',
      filterContent: filterComponents[FeatureViewsListColumnKeys.NUMBER_OF_FEATURES].filter,
      ...tableArrayLengthSortParameters(sortType, FeatureViewFCOFields.FEATURES, setSortType),
    },
    {
      name: 'Online',
      id: FeatureViewsListColumnKeys.ONLINE,
      content: (featureView) => {
        if (featureView[FeatureViewFCOFields.IS_ONLINE_MATERIALIZATION_ENABLED] === undefined) {
          return <Text size="s">n/a</Text>;
        }
        return (
          <MaterializationCell
            materialization={featureView[FeatureViewFCOFields.IS_ONLINE_MATERIALIZATION_ENABLED] ?? false}
          />
        );
      },
      columnWidth: 's',
      filterContent: filterComponents[FeatureViewsListColumnKeys.ONLINE].filter,
      ...tableBooleanSortParameters(sortType, FeatureViewFCOFields.IS_ONLINE_MATERIALIZATION_ENABLED, setSortType),
    },
    {
      name: 'Offline',
      id: FeatureViewsListColumnKeys.OFFLINE,
      content: (featureView) => {
        if (featureView[FeatureViewFCOFields.IS_OFFLINE_MATERIALIZATION_ENABLED] === undefined) {
          return <Text size="s">n/a</Text>;
        }
        return (
          <MaterializationCell
            materialization={featureView[FeatureViewFCOFields.IS_OFFLINE_MATERIALIZATION_ENABLED] ?? false}
          />
        );
      },
      columnWidth: 's',
      filterContent: filterComponents[FeatureViewsListColumnKeys.OFFLINE].filter,
      ...tableBooleanSortParameters(sortType, FeatureViewFCOFields.IS_OFFLINE_MATERIALIZATION_ENABLED, setSortType),
    },
    {
      name: 'Data Sources',
      id: FeatureViewsListColumnKeys.DATA_SOURCES,
      content: (featureView) => {
        const relatedDataSources = featureView[FeatureViewFCOFields.DATA_SOURCE_IDS].map((id) => idToFcoMap[id]);
        return <RelatedFCOsCell workspace={workspace} fcos={relatedDataSources} type={FCOType.DATA_SOURCE} />;
      },
      filterContent: filterComponents[FeatureViewsListColumnKeys.DATA_SOURCES].filter,
      ...tableArrayLengthSortParameters(sortType, FeatureViewFCOFields.DATA_SOURCE_IDS, setSortType),
      columnWidth: 'm',
    },
    {
      name: 'Entities',
      id: FeatureViewsListColumnKeys.ENTITIES,
      content: (featureView) => {
        const relatedEntities = featureView[FeatureViewFCOFields.ENTITY_IDS].map((id) => idToFcoMap[id]);
        return <RelatedFCOsCell workspace={workspace} fcos={relatedEntities} type={FCOType.ENTITY} />;
      },
      filterContent: filterComponents[FeatureViewsListColumnKeys.ENTITIES].filter,
      ...tableArrayLengthSortParameters(sortType, FeatureViewFCOFields.ENTITY_IDS, setSortType),
      columnWidth: 'm',
    },
    {
      name: 'Transformations',
      id: FeatureViewsListColumnKeys.TRANSFORMATIONS,
      content: (featureView) => {
        const relatedTransformations = featureView[FeatureViewFCOFields.ALL_TRANSFORMATIONS].map(
          (id) => idToFcoMap[id]
        );
        return <RelatedFCOsCell workspace={workspace} fcos={relatedTransformations} type={FCOType.TRANSFORMATION} />;
      },
      filterContent: filterComponents[FeatureViewsListColumnKeys.TRANSFORMATIONS].filter,
      ...tableArrayLengthSortParameters(sortType, FeatureViewFCOFields.ALL_TRANSFORMATIONS, setSortType),
      columnWidth: 'm',
    },
    {
      name: 'Feature Services',
      id: FeatureViewsListColumnKeys.FEATURE_SERVICES,
      content: (featureView) => {
        const relatedFeatureServices = featureView[FeatureViewFCOFields.FEATURE_SERVICES].map((id) => idToFcoMap[id]);
        return <RelatedFCOsCell workspace={workspace} fcos={relatedFeatureServices} type={FCOType.FEATURE_SERVICE} />;
      },
      filterContent: filterComponents[FeatureViewsListColumnKeys.FEATURE_SERVICES].filter,
      ...tableArrayLengthSortParameters(sortType, FeatureViewFCOFields.FEATURE_SERVICES, setSortType),
      columnWidth: 'm',
    },
    {
      name: 'Last Updated By',
      id: FeatureViewsListColumnKeys.LAST_UPDATED_BY,
      content: (featureView) => (
        <Link
          avatar={{ name: featureView[FCOFields.LAST_MODIFIED_BY] }}
          name={featureView[FCOFields.LAST_MODIFIED_BY]}
          noHover
        />
      ),
      filterContent: filterComponents[FeatureViewsListColumnKeys.LAST_UPDATED_BY].filter,
      columnWidth: 'm',
      ...tableStringSortParameters(sortType, FCOFields.LAST_MODIFIED_BY, setSortType),
    },
    {
      name: 'Last Updated At',
      id: FeatureViewsListColumnKeys.LAST_UPDATED_AT,
      content: (featureView) => <DateCell date={featureView[FCOFields.LAST_MODIFIED_DATE]} />,
      filterContent: filterComponents[FeatureViewsListColumnKeys.LAST_UPDATED_AT].filter,
      ...tableDateSortParameters(sortType, FCOFields.LAST_MODIFIED_DATE, setSortType),
      columnWidth: 'm',
    },
    {
      name: 'Owner',
      id: FeatureViewsListColumnKeys.CREATED_BY,
      content: (featureView) => (
        <Link noHover avatar={{ name: featureView[FCOFields.OWNER] }} name={featureView[FCOFields.OWNER]} />
      ),
      filterContent: filterComponents[FeatureViewsListColumnKeys.CREATED_BY].filter,
      ...tableStringSortParameters(sortType, FCOFields.OWNER, setSortType),
    },
    {
      name: 'Created At',
      id: FeatureViewsListColumnKeys.CREATED_AT,
      content: (featureView) => <DateCell date={featureView[FCOFields.CREATED_DATE]} />,
      filterContent: filterComponents[FeatureViewsListColumnKeys.CREATED_AT].filter,
      ...tableDateSortParameters(sortType, FCOFields.CREATED_DATE, setSortType),
      columnWidth: 'm' as const,
    },
    ...(canShowServerGroups
      ? [
          {
            name: 'Transform Server Group',
            id: FeatureViewsListColumnKeys.SERVER_GROUP,
            content: (featureView: FeatureViewFCO) =>
              featureView[FeatureViewFCOFields.TRANSFORM_SERVER_GROUP_NAME] === undefined ? (
                <>-</>
              ) : (
                <Link
                  icon="TransformServerGroup"
                  name={featureView[FeatureViewFCOFields.TRANSFORM_SERVER_GROUP_NAME]}
                  url={`/repo/${workspace}/server-groups/transform/${
                    featureView[FeatureViewFCOFields.TRANSFORM_SERVER_GROUP_ID]
                  }`}
                />
              ),
            columnWidth: 'm' as const,
            filterContent: filterComponents[FeatureViewsListColumnKeys.SERVER_GROUP].filter,
            ...tableStringSortParameters(sortType, FeatureViewFCOFields.TRANSFORM_SERVER_GROUP_NAME, setSortType),
          },
        ]
      : []),
  ];

  const selectableColumns = columns.map((column) => {
    return { name: column.name as string, visible: true };
  });

  const [columnState, setColumnState] = useState<{ name: string; visible: boolean }[]>(selectableColumns);

  const columnsSelector = (
    <Select
      groups={[
        {
          items: columnState.map((column) => {
            return {
              name: column.name,
              asToggle: true,
              onClick: () => {
                const newColumnState = columnState.map((selectableColumn) => {
                  if (selectableColumn.name !== column.name) {
                    return selectableColumn;
                  }

                  return {
                    name: selectableColumn.name,
                    visible: !selectableColumn.visible,
                  };
                });
                setColumnState && setColumnState(newColumnState);
              },
              selected: column.visible,
              selectable: true,
            };
          }),
        },
      ]}
      filterable={false}
    />
  );

  const filterEmpty: EmptyProps = {
    actions: <></>,
    placement: 'full-page',
    useCase: 'empty',
    title: 'No Results',
    description: 'No Feature Views match the current filters. Try adjusting your filters or search query.',
  };

  // Memoize the filtered columns to prevent recalculation
  const filteredColumns = useMemo(
    () =>
      columns
        .map((column) => {
          const matching = columnState.find((stateColumn) => stateColumn.name === column.name);
          if (!matching || matching?.visible !== true) {
            return undefined;
          }

          const asColumn: ExtendedTableColumn<FeatureViewFCO> = {
            ...column,
            isActive: column.id === activeHeading,
            setIsActive: (arg: boolean) => {
              if (arg) {
                setActiveHeading(column.id as FeatureViewsListColumnKeys);
                return;
              }
              setActiveHeading(undefined);
            },
            hideCallback: () => {
              const newColumnState = columnState.map((selectableColumn) => {
                if (selectableColumn.name !== column.name) {
                  return selectableColumn;
                }

                return {
                  name: selectableColumn.name,
                  visible: !selectableColumn.visible,
                };
              });

              setColumnState && setColumnState(newColumnState);
            },
            columnsContent: columnsSelector,
          };

          return asColumn;
        })
        .filter((column): column is ExtendedTableColumn<FeatureViewFCO> => column !== undefined),
    [columns, columnState, activeHeading, columnsSelector]
  );

  return (
    <TableViewLayout
      searchInput={
        <TableSelectors
          hasAnyFilters={hasAnyFilters}
          activeFilterCount={activeFilterCount}
          handleResetFilters={handleResetFilters}
          categories={categories}
          onSearchInputChange={onSearchInputChange}
        />
      }
      filterAndColumnControls={<TableColumnControls columnsSelector={columnsSelector} />}
      table={
        <>
          <Table
            items={filteredResults}
            columns={filteredColumns}
            frozenFirstColumn={true}
            emptyProps={filterEmpty}
            memoized={true}
          />
        </>
      }
    />
  );
};

export default FeatureViewsList;
